import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TableFooter from "./TableFooter";
import Table from "./Table";
import { statusStyleMap } from "./constants";
import TopFilter from "./TopFilter";
import FormSelect from "../common/FormSelect";
import { DropDownBold } from "../AdminIcons";
import CreateUserModal from "./CreateUserModal";
import CreateRoleModal from "./CreateRoleModal";
import AssignRoleModal from "./AssignRoleModal";
import ChangeRoleModal from "./ChangeRoleModal";
import UpdateRoleModal from "./UpdateRoleModal";
import {
  useToggleStaffStatusMutation,
  useRemoveStaffWithPendingStatusMutation,
  useDeleteRoleMutation,
  useChangeStudentStatusMutation,
} from "../../services/user/userApi";
import { getApiErrorMessage, showToast } from "../../utils/toast";
import { useChangeInstructorStatusMutation } from "../../services/instructor/instructorApi";

const ManageUserContent = ({
  activeTab,
  rows,
  pageRows,
  totalCount,
  tableFilters,
  activeFilter,
  onFilterChange,
  selectedRows,
  onToggleRow,
  onToggleAll,
  allVisibleSelected,
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  isTableLoading = false,
  roleFilterOptions = [],
  isRoleFilterLoading = false,
  isInvitedUserFilter = false,
  isAddedRoleFilter = false,
  accessRoleNameFilter,
  onAccessRoleNameChange,
  accessRoleStatusFilter,
  onAccessRoleStatusChange,
  onAccessRoleUpdated,
  onRolesListUpdated,
}) => {
  const navigate = useNavigate();
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false);
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [isUpdateRoleModalOpen, setIsUpdateRoleModalOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [toggleStaffStatus] = useToggleStaffStatusMutation();
  const [removeStaffWithPendingStatus] =
    useRemoveStaffWithPendingStatusMutation();
  const [deleteRole] = useDeleteRoleMutation();
  const [changeStudentStatus] = useChangeStudentStatusMutation();
  const [changeInstructorStatus] = useChangeInstructorStatusMutation();
  const handleChangeRole = (row) => {
    setChangeRoleUser(row);
    setIsChangeRoleModalOpen(true);
  };

  const handleEditRole = (row) => {
    setEditRole(row);
    setIsUpdateRoleModalOpen(true);
  };

  const handleRemoveRole = async (row) => {
    try {
      await deleteRole({ roleId: row.id }).unwrap();
      await onRolesListUpdated?.();
      showToast({
        type: "success",
        message: "Role removed successfully.",
      });
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to remove role. Please try again.",
        ),
      });
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      await toggleStaffStatus({ staffId: row.id }).unwrap();
      await onAccessRoleUpdated?.();
      showToast({
        type: "success",
        message: "User status updated successfully.",
      });
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to update status. Please try again.",
        ),
      });
    }
  };

  const handleViewStudentProfile = (row) => {
    const courseLabel = Array.isArray(row.course)
      ? row.course[0] || "-"
      : row.course || "-";

    navigate(`/admin/manage-user/student-profile/${row.id}`, {
      state: {
        student: {
          id: row.id,
          name: row.name,
          email: row.email,
          status: row.status,
          course: courseLabel,
          profilePicture: row.profilePicture,
        },
      },
    });
  };

  const handleViewInstructorProfile = (row) => {
    const courseLabel = Array.isArray(row.course)
      ? row.course.join(", ") || "-"
      : row.course || "-";

    navigate(`/admin/manage-user/instructor-profile/${row.id}`);
  };

  const handleViewStudentProgress = (row) => {
    const courseLabel = Array.isArray(row.course)
      ? row.course[0] || "-"
      : row.course || "-";

    navigate(`/admin/student-progress/student-overview/${row.id}`, {
      state: {
        student: {
          studentId: row.id,
          id: row.id,
          name: row.name,
          email: row.email,
          course: courseLabel,
        },
      },
    });
  };

  const handleToggleStudentStatus = async (row) => {
    try {
      const accountStatus = row.status === "active" ? "inactive" : "active";

      await changeStudentStatus({
        studentId: row.id,
        accountStatus,
      }).unwrap();

      showToast({
        type: "success",
        message: "Student status updated successfully.",
      });
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to update student status. Please try again.",
        ),
      });
    }
  };

  const handleToggleInstructorStatus = async (row) => {
    try {
      await changeInstructorStatus({
        instructorId: row.id,
        accountStatus: row.nextStatus,
      }).unwrap();

      showToast({
        type: "success",
        message: "Instructor status updated successfully.",
      });
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to update instructor status. Please try again.",
        ),
      });
    }
  };

  const handleRemovePendingStaff = async (row) => {
    try {
      await removeStaffWithPendingStatus({ email: row.userEmail }).unwrap();
      await onAccessRoleUpdated?.();
      showToast({
        type: "success",
        message: "User removed successfully.",
      });
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to remove user. Please try again.",
        ),
      });
    }
  };

  const columnsByTab = {
    Instructors: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      {
        key: "course",
        label: "Courses",
        headerClassName: "px-4 py-2.5 w-[360px]",
        cellClassName: "px-4 py-3 align-middle w-[360px] max-w-[360px]",
      },
      { key: "status", label: "Status" },
    ],
    Students: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      {
        key: "course",
        label: "Courses",
        headerClassName: "px-4 py-2.5 w-[360px]",
        cellClassName: "px-4 py-3 align-middle w-[360px] max-w-[360px]",
      },
      { key: "status", label: "Status" },
    ],
    "Access Role": [
      { key: "userEmail", label: "User Email" },
      { key: "accessRole", label: "Access Role" },
      { key: "status", label: "Status" },
    ],
  };

  const addedRoleColumns = [
    { key: "roleName", label: "Role Name" },
    {
      key: "description",
      label: "Description",
      cellClassName: "px-4 py-3 align-middle max-w-[340px]",
    },
    {
      key: "routePermissions",
      label: "Route Permissions",
      headerClassName: "px-4 py-2.5 w-[280px]",
      cellClassName: "px-4 py-3 align-middle w-[280px]",
    },
  ];

  const tableColumns =
    activeTab === "Access Role" && isAddedRoleFilter
      ? addedRoleColumns
      : columnsByTab[activeTab];

  const showAccessRoleFilters =
    activeTab === "Access Role" && !isAddedRoleFilter;

  const totalRowCount = totalCount ?? rows.length;

  return (
    <div>
      <TopFilter
        filters={tableFilters}
        activeFilter={activeFilter}
        onFilterChange={onFilterChange}
      />

      {activeTab === "Access Role" ? (
        <div className="mt-11 ml-4">
          <div className="flex items-end justify-between gap-4 pr-4">
            {showAccessRoleFilters ? (
              <div className="flex items-center gap-3">
                <FormSelect
                  label="Role"
                  labelClassName="text-sm"
                  id="access-role-filter"
                  options={roleFilterOptions}
                  defaultValue={accessRoleNameFilter || "All"}
                  placeholder={isRoleFilterLoading ? "Loading..." : "Role"}
                  dropdownClassName="w-52 absolute left-0"
                  controlWrapperClassName="mt-0"
                  selectClassName="h-[29px] rounded-lg border border-[#938A96] px-3 gap-8 text-sm text-black"
                  onChange={(value) => {
                    if (typeof onAccessRoleNameChange === "function") {
                      onAccessRoleNameChange(
                        value === "All" ? "" : value || "",
                      );
                    }
                  }}
                />

                {!isInvitedUserFilter ? (
                  <FormSelect
                    label="Status"
                    labelClassName="text-sm"
                    id="access-status-filter"
                    options={[
                      { value: "All", label: "All" },
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                      { value: "pending", label: "Pending" },
                    ]}
                    defaultValue={accessRoleStatusFilter || "All"}
                    placeholder="Status"
                    controlWrapperClassName="mt-0"
                    selectClassName="h-[29px]  rounded-lg border border-[#938A96] gap-8 px-3  text-sm text-black"
                    dropdownClassName="w-[98px]"
                    onChange={(value) => {
                      if (typeof onAccessRoleStatusChange === "function") {
                        onAccessRoleStatusChange(
                          value === "All" ? "" : value || "",
                        );
                      }
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <div />
            )}

            <div className={showAccessRoleFilters ? "" : "ml-auto"}>
              <FormSelect
                id="access-role-actions"
                options={[
                  { value: "create-user", label: "Create User" },
                  { value: "create-role", label: "Create Role" },
                  { value: "assign-role", label: "Assign Role" },
                ]}
                defaultValue=""
                placeholder="Add New"
                placeholderClassName="!text-white"
                keepPlaceholderOnSelect
                controlWrapperClassName="mt-0"
                selectClassName="h-8 rounded-lg border-0 !bg-[#923D8C] px-4 text-lg font-medium !text-white"
                dropdownClassName="w-[180px]"
                icon={DropDownBold}
                onChange={(value) => {
                  if (value === "create-user") {
                    setIsCreateUserModalOpen(true);
                  }

                  if (value === "create-role") {
                    setIsCreateRoleModalOpen(true);
                  }

                  if (value === "assign-role") {
                    setIsAssignRoleModalOpen(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-[30px]">
        <Table
          key={`${activeTab}-${activeFilter}-${currentPage}`}
          columns={tableColumns}
          rows={pageRows}
          isLoading={isTableLoading}
          selectedRows={selectedRows}
          onToggleRow={onToggleRow}
          onToggleAll={onToggleAll}
          allVisibleSelected={allVisibleSelected}
          statusStyleMap={statusStyleMap}
          showActions
          actionsMenuVariant={
            activeTab === "Access Role"
              ? isAddedRoleFilter
                ? "addedRole"
                : "accessRole"
              : activeTab === "Students"
                ? "student"
                : activeTab === "Instructors"
                  ? "instructor"
                  : null
          }
          onChangeRole={handleChangeRole}
          onEdit={handleEditRole}
          onRemove={
            isAddedRoleFilter ? handleRemoveRole : handleRemovePendingStaff
          }
          onToggleStatus={
            activeTab === "Students"
              ? handleToggleStudentStatus
              : activeTab === "Instructors"
                ? handleToggleInstructorStatus
                : handleToggleStatus
          }
          onViewProfile={
            activeTab === "Students"
              ? handleViewStudentProfile
              : activeTab === "Instructors"
                ? handleViewInstructorProfile
                : undefined
          }
          onViewProgress={handleViewStudentProgress}
        />

        <TableFooter
          selectedCount={selectedRows.length}
          totalCount={totalRowCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={onPrevious}
          onNext={onNext}
          prevDisabled={currentPage <= 1}
          nextDisabled={currentPage >= totalPages}
        />
      </div>

      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
      />

      <CreateRoleModal
        isOpen={isCreateRoleModalOpen}
        onSuccess={onRolesListUpdated}
        onClose={() => setIsCreateRoleModalOpen(false)}
      />

      <AssignRoleModal
        key={isAssignRoleModalOpen}
        isOpen={isAssignRoleModalOpen}
        onClose={() => setIsAssignRoleModalOpen(false)}
      />

      <ChangeRoleModal
        key={changeRoleUser?.id || "change-role"}
        isOpen={isChangeRoleModalOpen}
        user={changeRoleUser}
        onSuccess={onAccessRoleUpdated}
        onClose={() => {
          setIsChangeRoleModalOpen(false);
          setChangeRoleUser(null);
        }}
      />

      <UpdateRoleModal
        key={editRole?.id || "update-role"}
        isOpen={isUpdateRoleModalOpen}
        role={editRole}
        onSuccess={onRolesListUpdated}
        onClose={() => {
          setIsUpdateRoleModalOpen(false);
          setEditRole(null);
        }}
      />
    </div>
  );
};

export default ManageUserContent;
